/**
 * Locate a single entity inside a Game Pass success envelope, returning `null`
 * when none of the known shapes carry a payload.
 *
 * The backend is not perfectly consistent about where the entity lives, so we
 * accept every shape it has shipped:
 *   - `{ <key>: {...} }`            — canonical
 *   - `{ data: {...} }`             — generic envelope
 *   - `{ data: { <key>: {...} } }`  — nested envelope (matches
 *                                     `unwrapGamePassCollection`)
 *
 * A bare `{ success: true }` body or a `204 No Content` yields `null`.
 */
const findGamePassEntity = <T>(
  responseData: object | null | undefined,
  key: string,
): T | null => {
  if (responseData == null || typeof responseData !== "object") {
    return null;
  }
  const record = responseData as Record<string, unknown>;
  const keyedPayload = record[key] as T | undefined;
  const dataPayload = record.data;

  let nestedPayload: T | undefined;
  if (dataPayload && typeof dataPayload === "object") {
    nestedPayload = (dataPayload as Record<string, unknown>)[key] as
      | T
      | undefined;
  }

  return keyedPayload ?? nestedPayload ?? (dataPayload as T | undefined) ?? null;
};

/**
 * Strict unwrap: returns the entity or throws when the envelope carries no
 * payload. Used by the season mutation hooks, where a missing body genuinely
 * indicates an anomalous response the caller cannot recover from.
 *
 * Now also resolves the nested `{ data: { <key>: {...} } }` envelope (it
 * previously threw on that shape).
 */
export const unwrapGamePassEntity = <T>(
  responseData: object | null | undefined,
  key: string,
): T => {
  const payload = findGamePassEntity<T>(responseData, key);
  if (payload == null) {
    throw new Error(`Game Pass response missing ${key} payload`);
  }
  return payload;
};

/**
 * Tolerant unwrap: returns the entity, or `null` when the envelope carries no
 * payload (FE-1692). Used by the task / challenge create hooks: the POST has
 * already succeeded by the time we parse the body, so a missing / unrecognised
 * payload must NOT be treated as a creation failure (which surfaced a
 * misleading error toast even though the record was saved). Callers fall back
 * to a successful create with an unknown server id.
 */
export const unwrapGamePassEntityOrNull = <T>(
  responseData: object | null | undefined,
  key: string,
): T | null => findGamePassEntity<T>(responseData, key);

export const unwrapGamePassCollection = <T>(
  responseData: object,
  key: string,
): T[] => {
  const record = responseData as Record<string, unknown>;
  const keyedPayload = record[key];
  if (Array.isArray(keyedPayload)) {
    return keyedPayload as T[];
  }

  const dataPayload = record.data;
  if (Array.isArray(dataPayload)) {
    return dataPayload as T[];
  }

  if (dataPayload && typeof dataPayload === "object") {
    const nested = (dataPayload as Record<string, unknown>)[key];
    if (Array.isArray(nested)) {
      return nested as T[];
    }
  }

  throw new Error(`Game Pass response missing ${key} payload`);
};
